import React from 'react';
import { useImmer } from 'use-immer';
import { EventDefinition } from '../../../core';
import { EventsProviderProps, EventsProvider as PluginEventsProvider } from '../../../plugin-system';
import { UseEventQueryOptions } from '../../../plugin-system/runtime/EventsProvider/events-query';

type EventsProviderArgs = {
  /**
   * Initial event definitions, controls the initial state of the events
   */
  initialEventDefinitions?: EventDefinition[];
};

function useEventStore({ initialEventDefinitions = [] }: EventsProviderArgs) {
  const [eventDefinitions, setEventDefinitions] = useImmer<EventDefinition[]>(initialEventDefinitions);

  /**
   * Toggles the enabled state of an event
   */
  const toggleEvent = (name: string, enabled: boolean) => {
    setEventDefinitions((draft) => {
      const event = draft.find((a) => a.spec.name === name);
      if (event) {
        event.spec.enabled = enabled;
      }
    });
  };

  return {
    eventDefinitions,
    setEventDefinitions,
    toggleEvent,
  };
}

export function useEventStoreContext() {
  const context = React.useContext(EventsContext);
  if (context === undefined) {
    throw new Error('useEventStoreContext must be used within a DashboardEventsProvider');
  }
  return context;
}

const EventsContext = React.createContext<ReturnType<typeof useEventStore> | undefined>(undefined);

type DashboardEventsProviderProps = {
  initialEventDefinitions?: EventDefinition[];
  options?: UseEventQueryOptions;
  children: React.ReactNode;
  EventDrawers?: React.ReactNode;
  events?: EventsProviderProps['events'];
};

/**
 * Provides the event store and the ability to toggle events
 */
export function DashboardEventsProvider({
  children,
  options,
  initialEventDefinitions = [],
  EventDrawers,
  events,
}: DashboardEventsProviderProps) {
  const store = useEventStore({ initialEventDefinitions });

  return (
    <PluginEventsProvider eventDefinitions={store.eventDefinitions} options={options} events={events}>
      <EventsContext.Provider value={store}>
        {EventDrawers}
        {children}
      </EventsContext.Provider>
    </PluginEventsProvider>
  );
}
